/**
 * Audio format enum for TTS messages
 */
export enum AudioFormat {
  WAV = 'wav',
  MP3 = 'mp3',
  OGG = 'ogg',
  WEBM = 'webm',
  UNKNOWN = 'unknown',
  PCM_F32LE = 'pcm_f32le',
  RAW = 'raw',
  OPUS = 'opus',
  PCM = 'pcm'
}

/**
 * Socket event names for TTS audio
 */
export enum TTSServiceEvents {
  /**
   * Unified event for all TTS audio data (both streaming and non-streaming)
   */
  AUDIO_RESULT = 'tts-audio-result',
  
  /**
   * @deprecated Use AUDIO_RESULT instead
   * Legacy event for complete TTS audio results
   */
  TRANSCRIPT_RESULT = 'room-transcript-result',
  
  /**
   * @deprecated Use AUDIO_RESULT instead
   * Legacy event for streaming TTS audio chunks
   */
  STREAM_CHUNK = 'tts-stream-chunk',
  
  /**
   * Event for TTS processing errors
   */
  TRANSCRIPT_ERROR = 'room-transcript-error'
}

/**
 * Interface for TTS provider metadata
 * This extends the existing TTSAudioMessage with more detailed provider information
 */
export interface TTSProviderMetadata {
  /**
   * The TTS provider used (e.g., "deepgram", "openai")
   */
  provider: string;
  
  /**
   * Audio sample rate in Hz
   */
  sampleRate?: number;
  
  /**
   * Audio duration in seconds (if available)
   */
  duration?: number;
  
  /**
   * Time taken to generate the audio in milliseconds
   */
  processingTime?: number;
}

/**
 * Base audio data interface for TTS
 * Contains the core properties needed for audio playback
 */
export interface TTSAudioData {
  /**
   * Base64 encoded audio data (full buffer or chunk)
   */
  audioData: string;
  
  /**
   * Audio format (WAV, MP3, etc.)
   */
  format: AudioFormat;
  
  /**
   * Whether this is a streaming message
   */
  isStreaming: boolean;
  
  /**
   * ID for streaming chunks (required for streaming messages)
   */
  audioId: string;
  
  /**
   * Whether this is the final chunk (for streaming messages)
   */
  isFinal: boolean;
  
  /**
   * Sample rate of the audio (if known)
   */
  sampleRate?: number;
}

/**
 * Unified TTS audio message interface
 * Used for both streaming and non-streaming audio
 * Extends TTSAudioData with additional messaging properties
 */
export interface TTSAudioMessage extends TTSAudioData {
  /**
   * What kind of line this is. `announcement` marks a whole clip the server
   * said for the room, such as a game line (#4224): clients queue it behind
   * whatever is playing instead of letting it cut the companion off.
   */
  kind?: 'announcement';

  /**
   * The text content being spoken
   */
  text: string;
  
  /**
   * Timestamp when the message was created
   */
  timestamp: number;
  
  /**
   * ID of the sender (typically "ai-assistant")
   */
  userId: string;
  
  /**
   * Name of the sender (typically "AI Assistant")
   */
  userName: string;
  
  /**
   * Room ID the message belongs to
   */
  roomId: string;
  
  /**
   * Sample rate of the audio (if known)
   */
  sampleRate?: number;
  
  /**
   * Error message if there was an issue
   */
  error?: string;
  
  /**
   * Additional metadata about the TTS generation
   */
  metadata?: TTSProviderMetadata;
  
  /**
   * Sequence number for ordering chunks within an utterance
   */
  sequence?: number;

  /**
   * Yield group identifier for atomic host-ack + caller playout.
   */
  yieldId?: string;

  /**
   * Yield group position marker used by talk show queue ordering.
   */
  groupPosition?: 'host_ack' | 'caller' | 'post_ack';
}

/**
 * Type for TTS chunk handler function
 * Used for processing streaming audio chunks
 */
export type TTSChunkHandler = (data: TTSAudioMessage) => void;

/**
 * A single viseme event from TTS phoneme timestamps.
 * Used for phoneme-driven lip sync (Inworld TTS 1.5+).
 */
export interface VisemeEvent {
  /** Inworld viseme symbol: "aei", "o", "ee", "bmp", "fv", "lr", "th", "qw", "cdgknstxyz" */
  visemeSymbol: string;
  /** Start time in seconds, relative to utterance start */
  startTimeSeconds: number;
  /** Duration of this viseme in seconds */
  durationSeconds: number;
  /** IPA phoneme symbol (if available) */
  phoneSymbol?: string;
  /** The word this phoneme belongs to */
  word?: string;
}

/**
 * Payload for AGENT_VISEME_DATA socket events.
 * Sent from server to clients for phoneme-driven lip sync.
 */
export interface AgentVisemeDataPayload {
  /** Matches TTSAudioMessage.audioId for timeline correlation */
  audioId: string;
  agentId: string;
  /** Raw companion ID for compositor/Sixync consumers; agentId may be chat-attribution scoped. */
  companionId?: string;
  roomId: string;
  /** TTS provider that produced the timing data, when known. */
  provider?: string;
  /** Sorted by startTimeSeconds */
  visemes: VisemeEvent[];
  /** Whether this is the final batch of visemes for this utterance */
  isFinal: boolean;
  /** Sequence number for ordering within an utterance */
  sequence: number;
}

/**
 * Configuration options for TTS services
 */
export interface TTSServiceConfig {
  /**
   * The TTS provider to use
   */
  provider: 'deepgram' | 'openai' | 'cartesia' | string; // <-- Added 'cartesia' here
  
  /**
   * Voice ID or name to use
   */
  voice?: string;
  
  /**
   * Model ID or name to use
   */
  model?: string;
  
  /**
   * Additional provider-specific options
   */
  options?: Record<string, any>;
  
  /**
   * ID for streaming chunks (required for streaming messages)
   */
  audioId?: string;
  
  /**
   * Whether this is the final chunk (for streaming messages)
   */
  isFinal?: boolean;
  
  /**
   * Sample rate of the audio (if known)
   */
  sampleRate?: number;
}

/** Progress used only to bound recovery, never as evidence of successful playback. */
export interface PlaybackProgress {
  sequence?: number;
  durationMs: number;
  hasAudio: boolean;
  isFinal?: boolean;
}

/** Conservative PCM16 mono estimate; compressed audio must use decoded duration instead. */
export function estimatePcm16DurationMs(byteLength: number, sampleRate: number): number {
  if (!Number.isFinite(byteLength) || byteLength <= 0 || !Number.isFinite(sampleRate) || sampleRate < 8000 || sampleRate > 192000) return 0;
  return Math.min(300_000, Math.ceil(byteLength * 1000 / (sampleRate * 2)));
}

/**
 * Bounded recovery clock for streamed speech. Distinct chunks extend the idle
 * allowance and account for buffered audio. Repeated receipts cannot renew it.
 * Callers supply their local clock; timestamps never cross machines.
 */
export class PlaybackDeadline {
  private readonly sequences = new Set<number>();
  private finalSeen = false;
  private started = false;
  private available = false;
  private startedAt?: number;
  private durationMs = 0;
  private deadline: number;
  readonly hardDeadline: number;

  constructor(readonly createdAt: number, readonly idleMs = 45_000, readonly maxAgeMs = 600_000) {
    this.hardDeadline = createdAt + maxAgeMs;
    this.deadline = Math.min(this.hardDeadline, createdAt + idleMs);
  }

  get expiresAt(): number { return this.deadline; }
  get bufferedDurationMs(): number { return this.durationMs; }

  observe(progress: PlaybackProgress, now: number): boolean {
    if (!Number.isFinite(now) || now >= this.hardDeadline || this.finalSeen) return false;
    let advanced = false;
    if (progress.hasAudio && Number.isSafeInteger(progress.sequence) && progress.sequence! >= 0
      && this.sequences.size < 8192 && !this.sequences.has(progress.sequence!)) {
      this.sequences.add(progress.sequence!);
      const duration = Number.isFinite(progress.durationMs) ? Math.max(0, Math.min(300_000, progress.durationMs)) : 0;
      this.durationMs = Math.min(300_000, this.durationMs + duration);
      advanced = true;
    }
    if (progress.isFinal) { this.finalSeen = true; advanced = true; }
    if (advanced) this.extend(now);
    return advanced;
  }

  /** Once the preceding speaker releases the floor, allow this stream to start. */
  allowPlayback(now: number): void {
    if (this.available) return;
    this.available = true;
    this.extend(now);
  }

  playbackStarted(now: number): boolean {
    if (this.started || now >= this.hardDeadline) return false;
    this.started = true;
    this.startedAt = now;
    this.extend(now);
    return true;
  }

  /** Include known time waiting behind a predecessor, within the absolute cap. */
  after(predecessorDeadline?: number): number {
    return Math.min(this.hardDeadline, Math.max(this.deadline,
      predecessorDeadline === undefined ? this.deadline : predecessorDeadline + this.durationMs + this.idleMs));
  }

  private extend(now: number): void {
    this.deadline = Math.min(this.hardDeadline, Math.max(this.deadline,
      now + this.idleMs, (this.startedAt ?? now) + this.durationMs + this.idleMs));
  }
}

/** Provider-aligned spoken word times, relative to the utterance's PCM samples. */
export interface SpeechWordTiming { word: string; startMs: number; endMs: number }
